import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import type { GetEditorialSearchResultsResponse } from '@/api';
import { getMinPrice } from '@/views/search/index.ts';
import { Constants } from '@/utilities/constants';

export interface EditorialMarketStats {
  count: number | null;
  minPriceAmount: number | null;
}

// Textes rédigés en admin ; les espaces réservés {count}, {minPrice} et {siteName} sont remplacés par les valeurs calculées
export const needsStats = (source: string) => source.includes('{count}') || source.includes('{minPrice}');

// Un bloc (paragraphe, puce ou titre) dont une statistique reste non résolue est retiré du rendu au lieu d'afficher
// les accolades, pour qu'un texte à statistiques reste publiable sur un emplacement qui n'en a pas
export const stripUnresolvedStatBlocks = (html: string): string => {
  if (!needsStats(html)) {
    return html;
  }

  return html.replace(/<(p|li|h[1-6])\b[^>]*>(?:(?!<\/\1>)[\s\S])*?\{(?:count|minPrice)\}(?:(?!<\/\1>)[\s\S])*?<\/\1>/gi, '').replace(/<(ul|ol)\b[^>]*>\s*<\/\1>/gi, '');
};

export const extractMarketStats = (data: GetEditorialSearchResultsResponse): EditorialMarketStats => {
  const projects = data.items;

  // Les déploiements d'API antérieurs au type généré ne renvoient pas encore count ni minimumSalePriceAmount
  const count = Number.isInteger(data.count) ? data.count : data.pages <= 1 ? projects.length : null;

  let minPriceAmount: number | null;
  if (typeof data.minimumSalePriceAmount === 'number' || data.minimumSalePriceAmount === null) {
    minPriceAmount = data.minimumSalePriceAmount;
  } else {
    const saleAmounts = projects
      .filter((project) => project.isPaid)
      .map((project) => getMinPrice(project))
      .filter((price): price is NonNullable<typeof price> => !!price && price.type.id === Constants.PriceTypeId.ForSale)
      .map((price) => price.amount);
    minPriceAmount = saleAmounts.length > 0 ? Math.min(...saleAmounts) : null;
  }

  return { count, minPriceAmount };
};

export function useEditorialMarketStats() {
  const { t, locale } = useI18n();

  // Statistiques de marché calculées depuis les résultats de recherche de la page (nombre de projets, prix plancher)
  const marketStats = ref<EditorialMarketStats | null>(null);

  const marketMinPrice = computed(() => {
    const amount = marketStats.value?.minPriceAmount;
    return amount ? amount.formatCurrency(locale.value) : null;
  });

  const resolveAvailablePlaceholders = (source: string): string => {
    let text = source.replaceAll('{siteName}', t('global.site-name'));

    const count = marketStats.value?.count;
    if (count != null) {
      text = text.replaceAll('{count}', String(count));
    }

    if (marketMinPrice.value !== null) {
      text = text.replaceAll('{minPrice}', marketMinPrice.value);
    }

    return text;
  };

  // Retourne null quand une statistique demandée est introuvable, pour ne jamais afficher un texte troué
  const resolvePlaceholders = (source: string): string | null => {
    const text = resolveAvailablePlaceholders(source);
    return needsStats(text) ? null : text;
  };

  return { marketStats, marketMinPrice, resolveAvailablePlaceholders, resolvePlaceholders };
}
